import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExportJob } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  exportDownloadUrl,
  isExportDownloadAvailable,
  useExportJob,
} from './export-centre-data';
import {
  deriveExportRowStatus,
  hasDownloadableFile,
  type ExportRowStatus,
} from './export-status-model';

/**
 * The Export centre table (task 23.5), rendering the §3.5 "Reports & Exports" row
 * pattern: Format · Content · Controls carried into the file · action. Only the two
 * Excel rows apply for Foundation — full backup and filtered view; the
 * PowerPoint-ready, PDF and workload-and-capacity rows are out of scope (UI spec
 * §0.1) and are not rendered.
 *
 * Each row owns its own export attempt: a click enqueues a job, the job is polled
 * at least every five seconds (R18.4) until it terminates, progress shows while it
 * runs, and the row offers a download on success or states that no file was
 * produced and the stored data is unchanged on failure/timeout (R14.11, R15.9,
 * R18.17).
 */

/** A single export format the centre can start. */
export interface ExportRowConfig {
  readonly id: 'full_backup' | 'filtered_view';
  readonly format: string;
  readonly content: string;
  readonly controls: string;
  /**
   * Starts the export and returns the initial job. Each row wires its own POST
   * (full-backup takes no body, filtered-view carries the current view state), so
   * the table stays agnostic of which endpoint a row calls.
   */
  readonly start: (onStarted: (job: ExportJob) => void) => void;
  /** True while this row's start POST is in flight. */
  readonly isStarting: boolean;
  /** True when this row's start POST itself failed. */
  readonly startFailed: boolean;
}

export interface ExportCentreProps {
  readonly rows: readonly ExportRowConfig[];
  /** The permission/filter context chips (§3.5 / Reports screen). */
  readonly contextChips: readonly string[];
}

export function ExportCentre({ rows, contextChips }: ExportCentreProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="gap-4 p-5">
      <div>
        <p className="text-[13px] font-semibold text-txt">{t('exportCentre.heading')}</p>
        <p className="text-muted-foreground mt-1 text-[12.3px]">{t('exportCentre.hint')}</p>
      </div>

      {contextChips.length > 0 ? (
        <div className="flex flex-wrap gap-[6px]">
          {contextChips.map((chip) => (
            <span
              key={chip}
              className="rounded-[12px] border border-[#CFE2F5] bg-[var(--accent-soft)] px-[10px] py-[3px] text-[11px] text-[var(--accent-hover)]"
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">
              {t('exportCentre.table.colFormat')}
            </TableHead>
            <TableHead className="text-[10.5px] uppercase">
              {t('exportCentre.table.colContent')}
            </TableHead>
            <TableHead className="text-[10.5px] uppercase">
              {t('exportCentre.table.colControls')}
            </TableHead>
            <TableHead className="text-right">
              <span className="sr-only">{t('exportCentre.table.colAction')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {t('exportCentre.table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => <ExportTableRow key={row.id} row={row} />)
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/** One export format row, owning the enqueue → poll → terminal lifecycle. */
function ExportTableRow({ row }: { row: ExportRowConfig }): React.JSX.Element {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string | null>(null);
  const jobQuery = useExportJob(jobId);
  const job = jobQuery.data ?? null;

  const status = deriveExportRowStatus({
    job,
    isStarting: row.isStarting,
    // A failed status poll is also a "no file" outcome the user must see.
    startFailed: row.startFailed || jobQuery.isError,
  });

  const handleStart = useCallback((): void => {
    row.start((started) => setJobId(started.jobId));
  }, [row]);

  const isRunning = status.kind === 'running';

  return (
    <TableRow>
      <TableCell className="text-[12.3px] font-semibold text-txt">{row.format}</TableCell>
      <TableCell className="text-muted-foreground text-[12.3px] whitespace-normal">
        {row.content}
      </TableCell>
      <TableCell className="text-muted-foreground text-[12.3px] whitespace-normal">
        {row.controls}
      </TableCell>
      <TableCell className="text-right align-top">
        <div className="flex flex-col items-end gap-[6px]">
          <Button
            type="button"
            size="sm"
            onClick={handleStart}
            disabled={isRunning}
            aria-label={`${t('exportCentre.action.generate')} — ${row.format}`}
          >
            {isRunning ? t('exportCentre.action.generating') : t('exportCentre.action.generate')}
          </Button>
          <ExportRowStatusView status={status} job={job} />
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The per-row live status. Uses `aria-live="polite"` so the polling updates and
 * the terminal outcome are announced without stealing focus (R18.6). The
 * failed/timed-out branches state that no file was produced and the stored data is
 * unchanged (R14.11, R15.9, R18.17); the succeeded branch offers the download
 * (R14.11) — or, while the serving endpoint is absent, the not-yet-available
 * notice (see `export-centre-data`).
 */
function ExportRowStatusView({
  status,
  job,
}: {
  status: ExportRowStatus;
  job: ExportJob | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  if (status.kind === 'idle') {
    return null;
  }

  if (status.kind === 'running') {
    return (
      <p
        className="text-muted-foreground max-w-[220px] text-[11px]"
        role="status"
        aria-live="polite"
      >
        {status.progressPercent > 0
          ? t('exportCentre.status.preparing', { percent: status.progressPercent })
          : t('exportCentre.status.queued')}
      </p>
    );
  }

  if (status.kind === 'succeeded') {
    return (
      <DownloadControl
        job={job}
        readyMessage={
          status.initiativeCount === null
            ? t('exportCentre.status.readyNoCount')
            : t('exportCentre.status.ready', { count: status.initiativeCount })
        }
      />
    );
  }

  // failed / timed_out / start_failed — the no-file-produced, data-unchanged path.
  const messageKey =
    status.kind === 'timed_out'
      ? 'exportCentre.status.timedOut'
      : status.kind === 'failed'
        ? 'exportCentre.status.failed'
        : 'exportCentre.status.startFailed';

  return (
    <Alert variant="destructive" className="max-w-[280px] px-3 py-2 text-left">
      <AlertDescription
        className="text-[11px] text-destructive"
        role="status"
        aria-live="polite"
      >
        {t(messageKey)}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The download control for a succeeded job (R14.11). When the serving endpoint
 * exists it is a real download link to the token URL; while the endpoint is absent
 * (the current state — see `export-centre-data`) it renders the "not yet
 * available" notice rather than a link that would 404, so the flow is wired
 * without fabricating a download.
 */
function DownloadControl({
  job,
  readyMessage,
}: {
  job: ExportJob | null;
  readyMessage: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  const downloadable = hasDownloadableFile(job);

  return (
    <div className="flex max-w-[240px] flex-col items-end gap-[4px]">
      <p className="text-[11px] text-[var(--a-ok-txt)]" role="status" aria-live="polite">
        {readyMessage}
      </p>
      {downloadable && isExportDownloadAvailable ? (
        <Button asChild size="xs" variant="outline">
          <a href={exportDownloadUrl(job.jobId, job.downloadToken)} download>
            {t('exportCentre.action.download')}
          </a>
        </Button>
      ) : (
        <p
          className={cn('text-muted-foreground text-right text-[10.5px]')}
          role="note"
        >
          {t('exportCentre.downloadUnavailable')}
        </p>
      )}
    </div>
  );
}
